import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { Schedule, Track, Vehicle } from '../DataTypes';

export interface TourMapStation {
  id: number;
  stationName: string;
  lat: number;
  lon: number;
}

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private trackToOpenInScheduleSubject = new Subject<Track>();
  moveTrackFromTracksToScheduleEvent$ = this.trackToOpenInScheduleSubject.asObservable();
  openTrackAsSchedule(track: Track) {
    this.trackToOpenInScheduleSubject.next(track);
  }

  private trackToOpenFromScheduleSubject = new Subject<{
    trackId?: number;
    trackParameters?: { [key: string]: any };
    trackName?: string;
  }>();
  openTrackFromScheduleEvent$ = this.trackToOpenFromScheduleSubject.asObservable();

  openTrackFromSchedule(
    trackId?: number,
    trackParameters?: { [key: string]: any },
    trackName?: string
  ) {
    this.trackToOpenFromScheduleSubject.next({ trackId, trackParameters, trackName });
  }

  private trackFromMapSubject = new Subject<any>();
  moveTrackFromMapToTracksEvent$ = this.trackFromMapSubject.asObservable();
  openTrackFromMap(data: any) {
    this.trackFromMapSubject.next(data);
  }

  private actionsForVehicle = new BehaviorSubject<{
    drivingActions: string[];
    stoppingActions: string[];
  }>({
    drivingActions: [],
    stoppingActions: []
  });
  actionsForVehicleChangedEvent$ = this.actionsForVehicle.asObservable();
  updateActionsForVehicle(data: any) {
    this.actionsForVehicle.next(data);
  }
  getLastActionsForVehicle() {
    return this.actionsForVehicle.getValue();
  }

  private simulationResults = new BehaviorSubject<any>(null);
  simulationResultsChangedEvent$ = this.simulationResults.asObservable();
  updateSimulationResults(data: any) {
    this.simulationResults.next(data);
  }
  getLastSimulationResults() {
    return this.simulationResults.getValue();
  }

  private simulatingSchedule = new BehaviorSubject<Schedule | null>(null);
  simulatingScheduleChangedEvent$ = this.simulatingSchedule.asObservable();
  updateSimulatingSchedule(data: Schedule | null) {
    this.simulatingSchedule.next(data);
  }

  /**
   * Persistent reference to the schedule of the most recent simulation. Unlike
   * `simulatingSchedule` (which is reset to null once a run completes), this is
   * kept so exports (e.g. PPTX) can read the track/schedule configuration after
   * the results are shown.
   */
  private lastSimulatedSchedule = new BehaviorSubject<Schedule | null>(null);
  setLastSimulatedSchedule(data: Schedule | null) {
    this.lastSimulatedSchedule.next(data);
  }
  getLastSimulatedSchedule(): Schedule | null {
    return this.lastSimulatedSchedule.getValue();
  }
  private selectedVehicle = new BehaviorSubject<Vehicle | null>(null);
  selectedVehicleChangedEvent$ = this.selectedVehicle.asObservable();
  updateSelectedVehicle(data: Vehicle) {
    this.selectedVehicle.next(data);
  }
  getSelectedVehicle(): Vehicle | null {
    return this.selectedVehicle.getValue();
  }

  private loadTourVehicleSubject = new Subject<Vehicle>();
  loadTourVehicleEvent$ = this.loadTourVehicleSubject.asObservable();
  private tourVehicleSnapshot: Vehicle | null = null;
  private hasTourVehicleSnapshot = false;

  loadTourVehicle(vehicle: Vehicle) {
    if (!this.hasTourVehicleSnapshot) {
      this.tourVehicleSnapshot = this.selectedVehicle.getValue();
      this.hasTourVehicleSnapshot = true;
    }
    this.loadTourVehicleSubject.next(vehicle);
  }

  private loadTourTrackSubject = new Subject<unknown>();
  loadTourTrackEvent$ = this.loadTourTrackSubject.asObservable();
  loadTourTrack(trackData: unknown) {
    this.loadTourTrackSubject.next(trackData);
  }

  private loadTourScheduleSubject = new Subject<Schedule>();
  loadTourScheduleEvent$ = this.loadTourScheduleSubject.asObservable();
  loadTourSchedule(schedule: Schedule) {
    this.loadTourScheduleSubject.next(schedule);
  }

  private loadTourMapRouteSubject = new Subject<{
    start: TourMapStation;
    end: TourMapStation;
  }>();
  loadTourMapRouteEvent$ = this.loadTourMapRouteSubject.asObservable();
  loadTourMapRoute(start: TourMapStation, end: TourMapStation) {
    this.loadTourMapRouteSubject.next({ start, end });
  }

  private clearTourMapRouteSubject = new Subject<void>();
  clearTourMapRouteEvent$ = this.clearTourMapRouteSubject.asObservable();

  private clearTourDataSubject = new Subject<void>();
  clearTourDataEvent$ = this.clearTourDataSubject.asObservable();
  clearTourData() {
    if (this.hasTourVehicleSnapshot) {
      this.selectedVehicle.next(this.tourVehicleSnapshot);
    }
    this.clearTourMapRouteSubject.next();
    this.clearTourDataSubject.next();
    this.tourVehicleSnapshot = null;
    this.hasTourVehicleSnapshot = false;
  }

  cancelSimulationEvent$ = new Subject<void>();
  cancelSimulation() {
    this.cancelSimulationEvent$.next();
  }

  openNewTrackEvent$ = new BehaviorSubject<void>(undefined);
  newTrackOpened() {
    this.openNewTrackEvent$.next(undefined);
  }
}
